<template>
    <ResponsiveDialog
        v-model="show"
        :max-width="480"
        :full-height="false"
        hide-default-close
        :card-color="iconButtons ? (cardColor || undefined) : errorCardColor"
        :header-color="iconButtons ? undefined : errorHeaderColor"
        :header-image="iconButtons ? '' : (oak ? oakIcon : brockIcon)"
        :header-image-overlap="!iconButtons"
        :header-title="iconButtons ? '' : $t('common.areYouSure')"
        framed
        card-class="skin-card"
        :class="{ 'confirm-dialog--important': important }"
    >
        <template v-if="iconButtons">
            <div v-if="steps !== null" class="confirm-message-pill" :style="pillStyle">
                {{ message }}
                {{ steps }} <v-icon color="black" size="26">mdi-shoe-print</v-icon>
            </div>
            <div v-else-if="pill" class="confirm-message-pill confirm-message-pill--text" :style="pillStyle">
                {{ message }}
            </div>
            <v-card-text
                v-else
                class="d-flex align-center justify-center flex-grow-1 text-center px-6 black--text"
                style="font-size: 24px; font-weight: 700;"
            >
                {{ message }}
            </v-card-text>
            <v-spacer/>
            <v-card-actions class="justify-center pb-8" style="gap: 40px;">
                <!-- Velikost stejna jako plovouci zaviraci krizek v ResponsiveDialog.vue (45x45 fab, ikona 29). -->
                <v-btn fab width="45" height="45" color="success" class="btn-shadow" @click="answer(true)">
                    <v-icon size="29" color="black">mdi-check-bold</v-icon>
                </v-btn>
                <v-btn fab width="45" height="45" color="warning" class="btn-shadow" @click="answer(false)">
                    <v-icon size="29" color="black">mdi-close</v-icon>
                </v-btn>
            </v-card-actions>
        </template>
        <!-- Vychozi rezim: stejna hlavicka+telo struktura + ikonova dvojice jako iconButtons, titulek jde pres headerTitle/headerIcon na ResponsiveDialogu. Vlastni krizek schovany (hide-default-close) - zavirani = Cancel ikona. -->
        <template v-else>
            <!-- padding-left rezervuje misto pro obrazek z hlavicky (128px, viz responsive-dialog-header__image--overlap), ten prekryva i kus tela pod hlavickou. -->
            <v-card-text
                class="d-flex align-center justify-center flex-grow-1 text-center confirm-message-overlap-pad"
                :class="$vuetify.theme.dark ? 'white--text' : 'black--text'"
                :style="isMobile ? 'font-size: 19px;' : 'font-size: 16px;'"
            >
                <!-- text + ikonka kroku musi byt v jednom wrapperu, jinak flex layout polozi ikonku jako samostatny sloupec misto navazani na konec vety po zalomeni. -->
                <div>
                    {{ message }}
                    <!-- cena kroku pres `steps` opt (misto rucniho emoji v textu) - dedi barvu z v-card-text vyse (black--text/white--text podle tmaveho rezimu). -->
                    <template v-if="steps !== null">
                        <span class="ml-1">{{ steps }}</span> <v-icon size="26" class="ml-1">mdi-shoe-print</v-icon>
                    </template>
                </div>
            </v-card-text>
            <v-card-actions class="justify-center pb-6" style="gap: 40px;">
                <!-- okOnly = cisté potvrzeni bez realne "ne" volby (napr. informacni hlaska), jinak obe ikony jako v iconButtons rezimu. -->
                <v-btn fab width="45" height="45" color="success" class="btn-shadow" @click="answer(true)">
                    <v-icon size="29" color="black">mdi-check-bold</v-icon>
                </v-btn>
                <v-btn v-if="!okOnly" fab width="45" height="45" color="warning" class="btn-shadow" @click="answer(false)">
                    <v-icon size="29" color="black">mdi-close</v-icon>
                </v-btn>
            </v-card-actions>
        </template>
    </ResponsiveDialog>
</template>

<script>
    import {isMobileMixin} from '@/utils/isMobileMixin';
    import ResponsiveDialog from '@/components/utils/ResponsiveDialog';
    import {flags} from '@/config/featureFlags';

    // Nahrada nativniho confirm(), promise-based pres ref:
    //   if (await this.$refs.confirmDialog.open('Opravdu?')) { ... }
    // Rezimy/opts (iconButtons, steps, pill, cardColor/pillColor, oak, okOnly, important) viz claude/components/layout-shared-utils.md.
    export default {
        name: 'ConfirmDialog',
        components: {ResponsiveDialog},
        mixins: [isMobileMixin],
        data() {
            return {
                show: false,
                message: '',
                // hlavicka vychoziho (ne-iconButtons) rezimu
                brockIcon: require('@/assets/pic/web/brock_suspicious.png'),
                // pro `oak` opt (pokemon-related potvrzeni); stejny avatar jako OakSkillHintPopup.vue/StartingNext.vue
                oakIcon: require('@/assets/pic/web/oak_avatar.png'),
                oak: false,
                // Vychozi rezim je natvrdo v `error` barevnem schematu appky (#EF5167), nezavisle na cardColor/pillColor (ty resi jen iconButtons rezim).
                errorHeaderColor: '#EF5167',
                iconButtons: false,
                okOnly: false,
                important: false,
                steps: null,
                pill: false,
                cardColor: '',
                pillColor: '#9FA8DA',
                pillShadowColor: 'rgba(48,63,159,0.35)',
                resolveFn: null,
            }
        },
        computed: {
            pillStyle() {
                return {
                    background: this.pillColor,
                    boxShadow: `0 6px 14px ${this.pillShadowColor}, 0 2px 4px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.5)`,
                };
            },
            // Musi byt computed (ne data) - reaguje na $vuetify.theme.dark, jde primo pres cardColor prop (inline :color na v-card), ne CSS tridu.
            errorCardColor() {
                return this.$vuetify.theme.dark ? '#4A1620' : '#FBDCE1';
            },
        },
        watch: {
            // zavreni klikem mimo dialog (nebo krizkem v ResponsiveDialogu) = cancel
            show(v) {
                if (!v && this.resolveFn) {
                    const resolve = this.resolveFn;
                    this.resolveFn = null;
                    resolve(false);
                }
            },
        },
        methods: {
            // Auto-confirm (Options.vue prepinac) obchazi dialog uplne, vrati rovnou "ano". Vyjimky: `noAutoConfirm` (dismiss pokemona) a `important` (evolve potvrzeni - evoluce je nevratna) - oddelene kategorie, at je do budoucna jde vypinat nezavisle na sobe.
            open(message, opts = {}) {
                if (flags.autoConfirm && !opts.noAutoConfirm && !opts.important) {
                    return Promise.resolve(true);
                }
                this.message = message;
                this.okOnly = !!opts.okOnly;
                this.oak = !!opts.oak;
                this.iconButtons = !!opts.iconButtons;
                this.steps = opts.steps ?? null;
                this.pill = !!opts.pill;
                this.cardColor = opts.cardColor || '';
                this.pillColor = opts.pillColor || '#9FA8DA';
                this.pillShadowColor = opts.pillShadowColor || 'rgba(48,63,159,0.35)';
                this.important = !!opts.important;
                this.show = true;
                return new Promise((resolve) => {
                    this.resolveFn = resolve;
                });
            },
            answer(value) {
                const resolve = this.resolveFn;
                this.resolveFn = null;
                this.show = false;
                if (resolve) {
                    resolve(value);
                }
            },
        },
    }
</script>

<style scoped>
/* Rezerva pro obrazek z hlavicky (128px, presahuje z hlavicky do tela). */
.confirm-message-overlap-pad {
    /* left:20px + sirka 128px + 20px mezera = 168px */
    padding-left: 168px !important;
}
.confirm-message-pill {
    margin: 28px auto 0 auto;
    max-width: 90%;
    text-align: center;
    /* Pilulka nese sytejsi barvu nez cardColor karty (stejny vztah jako titulek/vymyte telo v "Trainer skills" popupu, TrainerDetail.vue) - barva jde pres `pillStyle` computed, at cela paleta zustane v jedne barevne rodine podle volajiciho. */
    color: #000;
    font-size: 23px;
    font-weight: 700;
    padding: 8px 18px;
    /* var(--skin-pill-radius, 999px) - stejny mechanismus jako .jenny-dialog-action-pill/.mewtwo-dialog-action-pill. !important nutne - Vuetify na prvnim potomkovi karty vynucuje border-top-*-radius:inherit, ktere by jinak prebilo horni rohy pilulky. */
    border-radius: var(--skin-pill-radius, 999px) !important;
}
/* "pill" bez "steps" nese celou vetu misto kratkeho popisku + cisla, takze mensi font, at se vejde bez zbytecneho zalamovani. */
.confirm-message-pill--text {
    font-size: 17px;
    padding: 10px 22px;
}
/* Tlacitka musi byt nad obrazkem z hlavicky (z-index 5, viz .responsive-dialog-header__image--overlap v ResponsiveDialog.vue) - selektor musi byt `.v-card__actions` (Vuetify 2 rendruje <v-card-actions> jako <div class="v-card__actions">, ne `.v-card-actions`). */
.v-card__actions {
    position: relative;
    z-index: 6;
}
</style>
